import { useState, useEffect, useRef } from 'react';
import { Tag, Radio, Empty } from 'antd';
import { green } from '@ant-design/colors';
import { StyledModal, WithLoading, ThumbnailItem } from 'common/components';
import { mapFileTypeToImage } from 'utils/fileType';

const ProductImageModal = ({
  toggleProductImageModal,
  visible,
  onSubmitSelectedImages,
  productImageData,
  setSelected,
  selected,
  isProductImageModalVisible,
  isLoading,
  multiple = false,
}) => {
  const lastSelectedValue = useRef(null);

  useEffect(() => {
    if (visible && !lastSelectedValue.current) {
      lastSelectedValue.current = selected;
    }
  }, [visible, selected]);

  const onClickImageItem = (item) => {
    if (multiple) {
      handleMultipleSelection(item);
    } else {
      handleSingleSelection(item);
    }
  };

  const handleSingleSelection = (item) => {
    const isSelected = selected?.some(
      (value) => value === item?.['digitalAssetId']
    );
    if (!isSelected) {
      setSelected([item?.['digitalAssetId']]);
    } else {
      setSelected([]);
    }
  };

  const handleMultipleSelection = (item) => {
    const isSelected = selected?.some(
      (value) => value === item?.['digitalAssetId']
    );

    if (!isSelected) {
      setSelected((prev) => prev.concat(item?.['digitalAssetId']));
    } else {
      const selectTemp = selected.filter(
        (value) => value !== item?.['digitalAssetId']
      );
      setSelected(selectTemp);
    }
  };

  const handleCancelModal = () => {
    toggleProductImageModal();
    setSelected(lastSelectedValue.current);
    lastSelectedValue.current = null;
  };

  const handleOk = () => {
    onSubmitSelectedImages();
    lastSelectedValue.current = null;
  };

  return (
    <StyledModal
      title={
        <div>
          {multiple ? (
            <>
              Select product images <Tag color={green[6]}>MULTIPLE</Tag>
            </>
          ) : (
            'Select product image'
          )}
        </div>
      }
      visible={visible}
      width={900}
      onCancel={handleCancelModal}
      okText='Select'
      cancelText='Close'
      onOk={handleOk}
      destroyOnClose
    >
      <WithLoading loading={isLoading}>
        <ImageCategoryTab
          productImageData={productImageData}
          selected={selected}
          onClickImageItem={onClickImageItem}
          isProductImageModalVisible={isProductImageModalVisible}
          isLoading={isLoading}
        />
      </WithLoading>
    </StyledModal>
  );
};

const ImageCategoryTab = ({
  productImageData,
  selected,
  onClickImageItem,
  isProductImageModalVisible,
}) => {
  const [tabActive, setTabActive] = useState(null);

  useEffect(() => {
    const setDefaultTab = () => {
      if (productImageData && isProductImageModalVisible) {
        const defaultTab = productImageData?.[0]?.associateType;
        setTabActive(defaultTab);
      }
    };

    setDefaultTab();
  }, [isProductImageModalVisible, JSON.stringify(productImageData)]);

  return (
    <div
      style={{
        minHeight: 'min(60vh, 100px)',
        maxHeight: 'min(65vh, 800px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ padding: 10 }}>
        <Radio.Group
          defaultValue='a'
          buttonStyle='solid'
          onChange={(e) => setTabActive(e.target.value)}
          value={tabActive}
        >
          {productImageData?.map((imageCategoryItem) => {
            return (
              <Radio.Button
                key={imageCategoryItem?.associateType}
                value={imageCategoryItem?.associateType}
              >
                {imageCategoryItem?.associateType}
              </Radio.Button>
            );
          })}
        </Radio.Group>
      </div>
      {!productImageData?.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        productImageData?.map((imageCategoryItem) => {
          const isTabActive = tabActive === imageCategoryItem?.associateType;

          const imageList = imageCategoryItem?.assetInfos || [];

          return (
            <div
              className='scroller'
              style={{
                display: isTabActive ? 'grid' : 'none',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '8px',
                padding: '10px',
              }}
              key={imageCategoryItem?.associateType}
            >
              {imageList?.map((item, index) => {
                return (
                  <div key={item?.['digitalAssetId']}>
                    <ThumbnailItem
                      imageThumbnail={getImageSrc(item)}
                      hideLabel={true}
                      responsive={false}
                      selected={selected?.includes(item?.['digitalAssetId'])}
                      onClickItemGrid={onClickImageItem}
                      onClickCheckboxItem={onClickImageItem}
                      dataDetail={item}
                      style={{ marginTop: 0, marginLeft: 0 }}
                    />
                  </div>
                );
              })}
            </div>
          );
        })
      )}
    </div>
  );
};

const getImageSrc = (item) => {
  return (
    item?.thumb500 ||
    item?.thumb1000 ||
    item?.thumbFull ||
    mapFileTypeToImage(item?.fileType)
  );
};

export default ProductImageModal;
